import React from 'react';

function sigmaColor(sigma) {
  const abs = Math.min(Math.abs(sigma), 3);
  const t   = abs / 3;
  if (t <= 0.5) {
    const u = t * 2;
    return `rgb(${Math.round(56 + (255-56)*u)},${Math.round(212 + (193-212)*u)},${Math.round(48 + (7-48)*u)})`;
  } else {
    const u = (t - 0.5) * 2;
    return `rgb(${Math.round(255 + (220-255)*u)},${Math.round(193 + (53-193)*u)},${Math.round(7 + (69-7)*u)})`;
  }
}

function groupBySegment(data) {
  const map = new Map();
  for (const row of data) {
    if (!map.has(row.segment)) map.set(row.segment, []);
    map.get(row.segment).push(row);
  }
  for (const [, cells] of map) cells.sort((a, b) => a.cell - b.cell);
  // Sort segments in ascending cell-number order (1–15, 16–30, … 76–90)
  return new Map(
    [...map.entries()].sort(([, a], [, b]) => (a[0]?.cell ?? 0) - (b[0]?.cell ?? 0))
  );
}

export default function CellHeatmap({
  segmentAnalysis,
  labels = {},
  uptimePct,
  rectifierCount,
  uptimeVThreshold,
  uptimeIThreshold,
  title = 'Cell Health Heatmap',
}) {
  const data = segmentAnalysis || [];

  if (data.length === 0) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">{title}</span></div>
        <div className="empty-state" style={{ minHeight: 200 }}>
          <div className="empty-state-icon">🔲</div>
          <div className="empty-state-text">No analysis data</div>
        </div>
      </div>
    );
  }

  const segments = groupBySegment(data);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
        <span className="badge badge-blue">{data.length} cells</span>
      </div>

      <div style={{ padding: '12px 16px 4px' }}>
        {[...segments.entries()].map(([segLabel, cells]) => (
          <div key={segLabel} style={{ marginBottom: 14 }}>
            {/* Segment label — use project chart label if configured */}
            <div style={{ fontSize: 11, color: '#8892a0', fontWeight: 500, marginBottom: 5 }}>
              {labels[cells[0]?.segment_key] || segLabel}
            </div>
            {/* Cell grid — fills full width, one cell per column */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
              gap: 3,
            }}>
              {cells.map(row => {
                const isOff = (row.off_pct ?? 0) >= 40;
                return (
                  <div
                    key={row.cell_col}
                    className="heatmap-cell"
                    style={{
                      background: isOff ? '#2a3a4a' : sigmaColor(row.sigma_from_mean),
                      opacity:    isOff ? 0.7 : 1,
                    }}
                    title={
                      `${row.cell_col}\n` +
                      (isOff
                        ? `Rectifier OFF ${(row.off_pct ?? 0).toFixed(0)}% of the time\n`
                        : (row.cell_resistance_avg != null
                            ? `Avg: ${row.cell_resistance_avg.toFixed(3)} mΩ\nSeg avg: ${row.segment_avg.toFixed(3)} mΩ\n`
                            : `Avg: ${(row.cell_voltage_avg ?? 0).toFixed(3)} V\nSeg avg: ${row.segment_avg.toFixed(3)} V\n`) +
                          `${row.pct_from_mean >= 0 ? '+' : ''}${row.pct_from_mean.toFixed(1)}%  ·  ` +
                          `${row.sigma_from_mean >= 0 ? '+' : ''}${row.sigma_from_mean.toFixed(2)} σ`)
                    }
                  >
                    <span className="heatmap-cell-label">{row.cell}</span>
                    <span className="heatmap-cell-value">
                      {isOff
                        ? '—'
                        : row.cell_resistance_avg != null
                          ? row.cell_resistance_avg.toFixed(1)
                          : (row.cell_voltage_avg ?? 0).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="heatmap-legend">
        <span>0 σ</span>
        <div className="heatmap-legend-bar" />
        <span>3+ σ</span>
      </div>

      {/* Uptime */}
      <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #1e2a3a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {uptimePct != null
            ? <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span className="uptime-value">{uptimePct.toFixed(1)}%</span>
                <span className="uptime-label">uptime</span>
              </div>
            : <span className="uptime-label">No rectifier data</span>
          }
          {(uptimeVThreshold != null || uptimeIThreshold != null) && (
            <span style={{ fontSize: 11, color: '#6b7a8d' }}>
              V &gt; {uptimeVThreshold}V · I &gt; {uptimeIThreshold}A
            </span>
          )}
        </div>
        {rectifierCount > 0 && (
          <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 4 }}>
            {rectifierCount} rectifier{rectifierCount > 1 ? 's' : ''} · downtime rows excluded from all calculations
          </div>
        )}
      </div>
    </div>
  );
}
